import { useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { MarkdownContent } from "~/ui-rtk/components/chatbot/MarkdownContent"
import {
  AdCreativeApprovalStatus,
  useAdForgeGetCreativeQuery,
  useAdForgeGetReviewLinkMutation,
} from "~/ui-rtk/api/adForgeApi"
import {
  AdBlastCreativeTestResult,
  AdBlastJobResult,
  AnalyzeCreativeJobResult,
  AnalyzeCreativeResultRow,
  ImageGenJobResult,
  JobAdSetResult,
  JobRunView,
  MetaAdsQaFinding,
  MetaAdsQaResult,
  useListAdsQaDoneAdsQuery,
  useListAdsQaIgnoredAdsQuery,
  useMarkAdsQaDoneAdMutation,
  useMarkAdsQaIgnoredAdMutation,
  useUnmarkAdsQaDoneAdMutation,
  useUnmarkAdsQaIgnoredAdMutation,
} from "~/ui-rtk/api/jobsApi"
import { useApplyUrlTagsMutation } from "~/ui-rtk/api/paidMediaApi"
import type { AdCommentModerationResult } from "~/ui-rtk/api/commentModerationApi"
import { adsManagerLink } from "~/ui-rtk/utils/ads-manager-links"
import { formatNumber } from "~/ui-rtk/utils/format"

/**
 * Per-job-type rendering for the Jobs list + detail modal. Add a new job type
 * by giving it a label, a one-line `describeJob` case, and a `JobResultRows`
 * case — the modal/list chrome stays generic. Unknown types fall back to a
 * generic summary so nothing breaks when a new job type ships.
 */

const TYPE_LABELS: Record<string, string> = {
  "ad-blast": "Ad blast",
  "ad-forge-image-gen": "Image enhancement",
  "ad-creative": "Creative build",
  "budget-update": "Budget update",
  "meta-ads-qa": "Meta ads QA",
  "analyze-creative": "Creative analysis",
  "analyze-media": "Media analysis",
  "ad-comment-moderation": "Comment moderation",
  "ad-comment-moderation-sweep": "Comment moderation sweep",
  "onboarding-taxonomy-tagging": "Onboarding tag suggestions",
}

/** Result payload of a `budget-update` record job (echoes its input). */
export interface BudgetUpdateResult {
  platform?: string
  entity?: "campaign" | "adset"
  entityId?: string
  /** Cents. */
  dailyBudget?: number
  lifetimeBudget?: number
  budgetAmount?: number
  budgetType?: string
}

/** "$150/day", "$3k lifetime", … from a budget-update result (cents). */
export function budgetChangeLabel(
  result: BudgetUpdateResult | null | undefined,
): string | undefined {
  if (!result) return undefined
  const dollars = (cents: number) =>
    `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
  if (result.dailyBudget != null) return `${dollars(result.dailyBudget)}/day`
  if (result.lifetimeBudget != null) {
    return `${dollars(result.lifetimeBudget)} lifetime`
  }
  if (result.budgetAmount != null) {
    const kind = result.budgetType?.toLowerCase().replace(/_/g, " ")
    return `${dollars(result.budgetAmount)}${kind ? ` ${kind}` : ""}`
  }
  return undefined
}

export function getJobLabel(type: string): string {
  return TYPE_LABELS[type] ?? type
}

const KIND_NOUN: Record<string, string> = {
  ad: "ad",
  post: "post",
  creative: "creative",
  adCreative: "draft",
}

/** Short one-line description — used for the list's source cell + modal subtitle. */
export function describeJob(job: JobRunView): string | null {
  const total = job.result?.summary?.total
  if (job.type === "ad-blast") {
    const r = job.result as AdBlastJobResult | null
    if (r?.source) {
      const noun = KIND_NOUN[r.kind ?? ""] ?? "source"
      return `${noun} ${r.source}${total != null ? ` → ${total} ad set${total === 1 ? "" : "s"}` : ""}`
    }
    const n = r?.sources?.length
    if (n) {
      const suffix = r?.creativeTest ? " · creative test" : ""
      return `${n} sources${total != null ? ` → ${total} ads` : ""}${suffix}`
    }
    return null
  }
  if (job.type === "ad-forge-image-gen") {
    return total != null ? `${total} image${total === 1 ? "" : "s"}` : null
  }
  if (job.type === "analyze-creative") {
    const s = (job.result as AnalyzeCreativeJobResult | null)?.summary
    if (!s) return null
    const parts = [`${s.total} ad${s.total === 1 ? "" : "s"}`]
    if (s.reused) parts.push(`${s.reused} reused`)
    if (s.tagsApplied != null) {
      parts.push(
        `${s.tagsApplied} tag${s.tagsApplied === 1 ? "" : "s"} applied on ${s.tagged ?? 0}`,
      )
    }
    return parts.join(" · ")
  }
  if (job.type === "ad-creative") {
    const name = (job.result as { name?: string } | null)?.name
    return name ? `"${name}"` : null
  }
  if (job.type === "budget-update") {
    const result = job.result as BudgetUpdateResult | null
    if (!result) return null
    const change = budgetChangeLabel(result)
    return [`${result.entity ?? "entity"} ${result.entityId ?? ""}`, change]
      .filter(Boolean)
      .join(" → ")
  }
  if (job.type === "meta-ads-qa") {
    const r = job.result as unknown as MetaAdsQaResult | null
    if (!r) return null
    const hidden = r.ignored?.findingCount
      ? ` · ${r.ignored.findingCount} hidden`
      : ""
    return `${r.totalFindings} finding${r.totalFindings === 1 ? "" : "s"} across ${r.adsWithFindings}/${r.adsChecked} ads${hidden}`
  }
  if (job.type === "ad-comment-moderation") {
    const r = job.result as unknown as AdCommentModerationResult | null
    if (!r) return null
    if (r.skipped) return `skipped: ${r.reason ?? r.skipped}`
    const refused = r.hideFailed ? ` · ${r.hideFailed} refused` : ""
    return `${r.hidden} ${r.dryRun ? "flagged (hiding off)" : "hidden"}, ${r.kept} kept across ${r.postsScanned}/${r.postsFound} posts${refused}`
  }
  return null
}

function Field({ label, value }: { label: string; value?: string }) {
  if (!value) return null
  return (
    <div className="flex gap-1">
      <span className="text-system-gray-500">{label}:</span>
      <span className="font-mono text-system-gray-200">{value}</span>
    </div>
  )
}

function StatusPill({ ok, okLabel }: { ok: boolean; okLabel: string }) {
  if (ok) {
    return (
      <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[11px] text-emerald-300">
        {okLabel}
      </span>
    )
  }
  return (
    <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-[11px] text-rose-300">
      Failed
    </span>
  )
}

function EmptyRows() {
  return (
    <p className="text-sm text-system-gray-400">
      No per-item results recorded.
    </p>
  )
}

// ---------------------------------------------------------------- ad-blast ---

function AdSetRow({
  row,
  showSource,
}: {
  row: JobAdSetResult
  showSource: boolean
}) {
  const detail = row.result
  const newAdId = row.externalAdId || detail?.newAdId
  // "Open in Ads Manager" — Meta or TikTok, from the account the job recorded.
  const managerUrl = adsManagerLink({
    platform: row.platform,
    adAccountId: row.adAccountId,
    adIds: [newAdId],
    businessId: row.businessId,
    createdAt: detail?.createdAt,
  })
  // 1:1 blasts (MD-5426) name the ad set they created; a row whose creation
  // failed has no id at all, so the name + error is everything we can show.
  const warnings = row.warnings ?? []
  return (
    <tr className="border-b border-white/5 last:border-0">
      {showSource && (
        <td className="py-2 pr-3 align-top font-mono text-[11px] text-system-gray-400">
          {row.source ?? "—"}
        </td>
      )}
      <td className="py-2 pr-3 align-top">
        {row.adSetName && (
          <div className="flex items-center gap-1.5 text-xs text-system-gray-200">
            <span className="truncate" title={row.adSetName}>
              {row.adSetName}
            </span>
            {row.createdAdSet && (
              <span className="shrink-0 rounded-full bg-sky-500/20 px-1.5 py-px text-[10px] uppercase tracking-wide text-sky-300">
                new
              </span>
            )}
          </div>
        )}
        {row.adSetId && (
          <div className="font-mono text-xs text-system-gray-200">
            {row.adSetId}
          </div>
        )}
        {detail?.strategy && (
          <div className="text-[11px] text-system-gray-500">
            {detail.strategy}
          </div>
        )}
        {warnings.map((warning, i) => (
          <div key={i} className="text-[11px] text-amber-300">
            {warning}
          </div>
        ))}
      </td>
      <td className="py-2 pr-3 align-top">
        <StatusPill ok={row.success} okLabel="Created" />
      </td>
      <td className="py-2 align-top text-[11px] text-system-gray-300">
        {row.success ? (
          <div className="space-y-0.5">
            <Field label="Ad" value={newAdId} />
            {managerUrl && (
              <a
                href={managerUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-block text-brand hover:text-basic-pink-hover"
              >
                Open in Ads Manager ↗
              </a>
            )}
            <Field label="Creative" value={detail?.creativeId} />
            {detail?.newAdName && (
              <div className="truncate text-system-gray-500">
                {detail.newAdName}
              </div>
            )}
          </div>
        ) : (
          <span className="text-rose-300">{row.error ?? "Unknown error"}</span>
        )}
      </td>
    </tr>
  )
}

/**
 * Outcome of the creative-test phase of an ad-blast batch (MD-5569). The
 * study is Meta's object: there is no per-study Ads Manager URL, so the id is
 * shown for the Experiments tool and the status explains what happened.
 */
function CreativeTestBanner({ test }: { test: AdBlastCreativeTestResult }) {
  const tone =
    test.status === "created"
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
      : test.status === "pending"
        ? "border-white/10 bg-white/5 text-system-gray-300"
        : "border-rose-500/40 bg-rose-500/10 text-rose-200"
  const budget =
    test.spec.dailyBudgetCents != null
      ? `${formatNumber(test.spec.dailyBudgetCents / 100, { currency: true })}/day`
      : `${test.spec.lifetimeBudgetPercentage}% of lifetime budget`
  const days = Math.round((test.spec.endTime - test.spec.startTime) / 86_400)
  const label: Record<AdBlastCreativeTestResult["status"], string> = {
    pending: "Creative test pending",
    created: "Creative test published",
    skipped: "Creative test not created",
    failed: "Creative test refused by Meta",
  }
  return (
    <div className={`mb-3 rounded-md border px-3 py-2 text-xs ${tone}`}>
      <div className="font-medium">
        {label[test.status]} · {test.spec.name}
      </div>
      <div className="mt-0.5 text-[11px] opacity-80">
        {test.cells?.length ?? "—"} variants · {budget} · {days} day
        {days === 1 ? "" : "s"} · ad set {test.adSetId}
      </div>
      {test.studyId && (
        <div className="mt-0.5 font-mono text-[11px]">
          Study {test.studyId} — see Ads Manager › Experiments
        </div>
      )}
      {test.error && <div className="mt-0.5 text-[11px]">{test.error}</div>}
    </div>
  )
}

function AdBlastRows({ job }: { job: JobRunView }) {
  const result = job.result as AdBlastJobResult | null
  const rows = result?.results ?? []
  const sourceCount = result?.sources?.length ?? (result?.source ? 1 : 0)
  const showSource = sourceCount > 1
  if (rows.length === 0) return <EmptyRows />
  return (
    <>
      {result?.creativeTest && (
        <CreativeTestBanner test={result.creativeTest} />
      )}
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-white/10 text-[11px] uppercase tracking-wide text-system-gray-500">
            {showSource && <th className="py-2 pr-3 font-medium">Source</th>}
            <th className="py-2 pr-3 font-medium">Ad set / strategy</th>
            <th className="py-2 pr-3 font-medium">Status</th>
            <th className="py-2 font-medium">Ad &amp; creative / error</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <AdSetRow
              key={`${row.source ?? ""}-${row.platform}-${row.adSetId}-${i}`}
              row={row}
              showSource={showSource}
            />
          ))}
        </tbody>
      </table>
    </>
  )
}

// ------------------------------------------------------ ad-forge-image-gen ---

function ImageGenRows({ job }: { job: JobRunView }) {
  const result = job.result as ImageGenJobResult | null
  const rows = result?.results ?? []
  if (rows.length === 0) return <EmptyRows />
  return (
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="border-b border-white/10 text-[11px] uppercase tracking-wide text-system-gray-500">
          <th className="py-2 pr-3 font-medium">Image</th>
          <th className="py-2 pr-3 font-medium">Status</th>
          <th className="py-2 font-medium">New asset / error</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr
            key={`${row.parentAssetId}-${row.kind}-${row.slot ?? ""}-${i}`}
            className="border-b border-white/5 last:border-0"
          >
            <td className="py-2 pr-3 align-top">
              <div className="text-xs text-system-gray-200">
                {row.kind === "main"
                  ? "Enhanced main"
                  : `Placement · ${row.slot ?? row.aspectRatio}`}
              </div>
              <div className="font-mono text-[11px] text-system-gray-500">
                {row.parentAssetId}
              </div>
            </td>
            <td className="py-2 pr-3 align-top">
              <StatusPill ok={row.success} okLabel="Generated" />
            </td>
            <td className="py-2 align-top text-[11px] text-system-gray-300">
              {row.success ? (
                <span className="font-mono">{row.newAssetId ?? "—"}</span>
              ) : (
                <span className="text-rose-300">
                  {row.error ?? "Unknown error"}
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ------------------------------------------------------------- ad-creative ---

const APPROVAL_STATUS_META: Record<
  AdCreativeApprovalStatus,
  { label: string; className: string }
> = {
  DRAFT: { label: "Draft", className: "bg-white/10 text-system-gray-300" },
  PENDING_APPROVAL: {
    label: "Pending approval",
    className: "bg-amber-500/20 text-amber-300",
  },
  APPROVED: {
    label: "Approved",
    className: "bg-emerald-500/20 text-emerald-300",
  },
  CHANGES_REQUESTED: {
    label: "Changes requested",
    className: "bg-amber-500/20 text-amber-300",
  },
  REJECTED: { label: "Rejected", className: "bg-rose-500/20 text-rose-300" },
}

function CreativeField({
  label,
  value,
}: {
  label: string
  value?: string | null
}) {
  if (!value) return null
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-system-gray-500">
        {label}
      </div>
      <div className="text-sm text-system-gray-200">{value}</div>
    </div>
  )
}

/**
 * `ad-creative` record jobs point at a saved AdForge creative — show the
 * creative itself (live, not a snapshot): image, copy, approval state, and
 * the public review link.
 */
function AdCreativeRows({ job }: { job: JobRunView }) {
  const adCreativeId = (job.result as { adCreativeId?: string } | null)
    ?.adCreativeId
  const [copying, setCopying] = useState(false)
  const [getReviewLink] = useAdForgeGetReviewLinkMutation()

  const {
    data: creative,
    isLoading,
    isError,
  } = useAdForgeGetCreativeQuery(adCreativeId ?? "", {
    skip: !adCreativeId,
  })

  if (!adCreativeId) {
    return (
      <p className="text-sm text-system-gray-400">
        The creative reference becomes available once the job completes.
      </p>
    )
  }
  if (isLoading) {
    return <p className="text-sm text-system-gray-400">Loading creative…</p>
  }
  if (isError || !creative) {
    return (
      <p className="text-sm text-system-gray-400">
        This creative no longer exists (it may have been deleted).
      </p>
    )
  }

  const media = creative.mediaAssets?.[0]
  const imageUrl = media?.thumbnailUrl || media?.url
  const approval = APPROVAL_STATUS_META[creative.approvalStatus]

  const copyReviewLink = async () => {
    if (copying) return
    setCopying(true)
    try {
      const { token } = await getReviewLink(creative.id).unwrap()
      await navigator.clipboard.writeText(
        `${window.location.origin}/review/${token}`,
      )
      toast.success("Review link copied — anyone with it can review.")
    } catch {
      toast.error("Couldn't create a review link")
    } finally {
      setCopying(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4">
        {imageUrl && (
          <img
            src={imageUrl}
            alt={creative.name}
            className="h-28 w-28 shrink-0 rounded-lg border border-white/10 object-cover"
          />
        )}
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-white">
              {creative.name}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] ${approval.className}`}
            >
              {approval.label}
            </span>
          </div>
          <div className="text-[11px] text-system-gray-500">
            {creative.format} · {creative.platform}
            {creative.callToAction ? ` · ${creative.callToAction}` : ""}
          </div>
          {creative.reviewNote && (
            <div className="text-xs italic text-system-gray-400">
              Reviewer note: {creative.reviewNote}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <CreativeField label="Headline" value={creative.headline} />
        <CreativeField label="Description" value={creative.description} />
        <div className="sm:col-span-2">
          <CreativeField label="Primary text" value={creative.primaryText} />
        </div>
        <CreativeField
          label="Destination URL"
          value={creative.destinationUrl}
        />
        <CreativeField label="Display link" value={creative.displayLink} />
      </div>

      <div>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          disabled={copying}
          onClick={copyReviewLink}
        >
          {copying ? "Copying…" : "Copy review link"}
        </button>
      </div>
    </div>
  )
}

// ------------------------------------------------------------- meta-ads-qa ---

const SEVERITY_META: Record<
  MetaAdsQaFinding["severity"],
  { label: string; className: string }
> = {
  error: { label: "Error", className: "bg-rose-500/20 text-rose-300" },
  warning: { label: "Warning", className: "bg-amber-500/20 text-amber-300" },
  info: { label: "Info", className: "bg-sky-500/20 text-sky-300" },
}

/**
 * One-line UTM coverage for a QA run — the UTM check is the only part of the
 * QA that CHANGES something on Meta (url_tags rewritten to the company
 * pattern), so the run's detail always states what it did.
 */
function UtmCoverageLine({
  utms,
}: {
  utms: NonNullable<NonNullable<MetaAdsQaResult["coverage"]>["utms"]>
}) {
  if (utms.error) {
    return (
      <p className="text-[11px] text-amber-300">
        UTMs not checked — the company UTM pattern could not be read (
        {utms.error}).
      </p>
    )
  }
  if (utms.mode === "none") {
    return (
      <p className="text-[11px] text-system-gray-500">
        UTMs not checked — no UTM pattern configured (Settings → Company → UTM
        parameters). Nothing was rewritten.
      </p>
    )
  }
  const notFixed = utms.fixFailed + utms.unfixable + utms.notFixed
  return (
    <p className="text-[11px] text-system-gray-400">
      UTMs (
      <span className="font-mono text-system-gray-300">{utms.pattern}</span>
      ): {utms.compliant} of {utms.checked} ads compliant
      {utms.fixed > 0 && (
        <span className="text-emerald-300">
          {" "}
          · {utms.fixed} auto-fixed (url_tags rewritten)
        </span>
      )}
      {notFixed > 0 && (
        <span className="text-rose-300"> · {notFixed} wrong, not fixed</span>
      )}
      .
    </p>
  )
}

/** Copy for the Slack-link confirmation dialog, per mark kind. */
const PENDING_MARK_COPY = {
  irrelevant: {
    title: "Mark this ad irrelevant?",
    alreadyTitle: "Already marked irrelevant",
    body: "The ad keeps being QA-checked, but its findings are hidden from future reports. You can re-include it from the Ignored ads list.",
    alreadyBody:
      "Its findings are already hidden from future QA reports. Re-include it from the Ignored ads list below.",
    action: "Mark irrelevant",
  },
  done: {
    title: "Mark this ad done?",
    alreadyTitle: "Already marked done",
    body: "The ad is closed out: future QA runs skip it entirely (not reviewed, not reported). You can re-open it from the Done ads list.",
    alreadyBody:
      "Future QA runs already skip this ad. Re-open it from the Done ads list below.",
    action: "Mark done",
  },
} as const

/**
 * meta-ads-qa results: findings grouped per ad, with per-ad "Mark done" and
 * "Mark irrelevant" actions. Done ads are closed out — the row flips to done
 * right away and future runs skip the ad entirely. Irrelevant ads keep being
 * QA'd but their findings are hidden from future reports. The "Done ads" and
 * "Ignored ads" lists below are live (not snapshots) and are where marks are
 * undone.
 */
function MetaAdsQaRows({ job }: { job: JobRunView }) {
  const result = job.result as unknown as MetaAdsQaResult | null
  const { data: ignoredList = [] } = useListAdsQaIgnoredAdsQuery()
  const [markIgnored, { isLoading: marking }] = useMarkAdsQaIgnoredAdMutation()
  const [unmarkIgnored, { isLoading: unmarking }] =
    useUnmarkAdsQaIgnoredAdMutation()
  const { data: doneList = [] } = useListAdsQaDoneAdsQuery()
  const [markDoneAd, { isLoading: markingDone }] = useMarkAdsQaDoneAdMutation()
  const [unmarkDoneAd, { isLoading: reopening }] =
    useUnmarkAdsQaDoneAdMutation()
  const [applyUrlTags, { isLoading: applyingUtms }] = useApplyUrlTagsMutation()
  // `?irrelevant=<adId>` / `?done=<adId>` are the Slack report's "Mark
  // irrelevant" / "Mark done" links: they land on this run with the
  // confirmation for that ad already open.
  const [searchParams, setSearchParams] = useSearchParams()
  const pendingDoneId = searchParams.get("done")
  const pendingIrrelevantId = searchParams.get("irrelevant")
  const clearPendingMark = () =>
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.delete("irrelevant")
        next.delete("done")
        return next
      },
      { replace: true },
    )

  if (!result) {
    return (
      <p className="text-sm text-system-gray-400">
        Findings become available once the job completes.
      </p>
    )
  }

  const ignoredIds = new Set(ignoredList.map(ad => ad.adId))
  const doneIds = new Set(doneList.map(ad => ad.adId))
  const hiddenByAdId = new Map(
    (result.ignored?.ads ?? []).map(ad => [ad.adId, ad.hiddenFindings]),
  )

  const byAd = new Map<
    string,
    { adName: string; findings: MetaAdsQaFinding[] }
  >()
  for (const f of result.findings ?? []) {
    const entry = byAd.get(f.adId) ?? { adName: f.adName, findings: [] }
    entry.findings.push(f)
    byAd.set(f.adId, entry)
  }

  // UTM_MISMATCH findings carry the expected url_tags as `suggestion`. The
  // QA only suggests (rewriting is opt-in per schedule), so applying is a
  // click here — through the same bulk "Apply URL tags" endpoint the ads
  // table uses. Suggestions can differ per ad (an ad's extra keys are kept),
  // so calls are grouped by url_tags string, 50 ads per call (the DTO cap).
  const utmSuggestions = new Map<string, string>()
  for (const f of result.findings ?? []) {
    if (
      f.code === "UTM_MISMATCH" &&
      f.suggestion &&
      !ignoredIds.has(f.adId) &&
      !doneIds.has(f.adId)
    ) {
      utmSuggestions.set(f.adId, f.suggestion)
    }
  }
  const applyUtms = async (adIds: string[]) => {
    const byTags = new Map<string, string[]>()
    for (const adId of adIds) {
      const urlTags = utmSuggestions.get(adId)
      if (urlTags) byTags.set(urlTags, [...(byTags.get(urlTags) ?? []), adId])
    }
    let applied = 0
    let failed = 0
    try {
      for (const [urlTags, ids] of byTags) {
        for (let i = 0; i < ids.length; i += 50) {
          const res = await applyUrlTags({
            platform: "meta",
            data: {
              ads: ids.slice(i, i + 50).map(adId => ({ adId })),
              urlTags,
            },
          }).unwrap()
          applied += res.summary.successful
          failed += res.summary.failed
        }
      }
    } catch {
      toast.error("Couldn't apply the URL parameters")
      return
    }
    if (failed > 0) {
      toast.warning(
        `URL parameters applied to ${applied} ad${applied === 1 ? "" : "s"}, ${failed} rejected by Meta.`,
      )
    } else {
      toast.success(
        `URL parameters applied to ${applied} ad${applied === 1 ? "" : "s"} — the next QA run will confirm.`,
      )
    }
  }

  const mark = async (adId: string, adName?: string) => {
    try {
      await markIgnored({ adId, adName }).unwrap()
      toast.success("Marked irrelevant — future QA reports hide this ad.")
    } catch {
      toast.error("Couldn't mark the ad")
    }
  }
  const unmark = async (adId: string) => {
    try {
      await unmarkIgnored(adId).unwrap()
      toast.success("Re-included — future QA reports show this ad again.")
    } catch {
      toast.error("Couldn't re-include the ad")
    }
  }

  const markDone = async (adId: string, adName?: string) => {
    try {
      await markDoneAd({ adId, adName }).unwrap()
      toast.success("Marked done — future QA runs skip this ad.")
    } catch {
      toast.error("Couldn't mark the ad done")
    }
  }
  const reopen = async (adId: string) => {
    try {
      await unmarkDoneAd(adId).unwrap()
      toast.success("Re-opened — future QA runs review this ad again.")
    } catch {
      toast.error("Couldn't re-open the ad")
    }
  }

  // A `done` link wins if both params are somehow present.
  let pendingKind: keyof typeof PENDING_MARK_COPY | null = null
  if (pendingDoneId) pendingKind = "done"
  else if (pendingIrrelevantId) pendingKind = "irrelevant"
  const pendingId = pendingDoneId ?? pendingIrrelevantId
  const pendingMark =
    pendingKind && pendingId
      ? {
          kind: pendingKind,
          adId: pendingId,
          adName:
            byAd.get(pendingId)?.adName ??
            result.ignored?.ads.find(ad => ad.adId === pendingId)?.adName,
          alreadyMarked:
            pendingKind === "done"
              ? doneIds.has(pendingId)
              : ignoredIds.has(pendingId),
        }
      : null
  const pendingCopy = pendingMark ? PENDING_MARK_COPY[pendingMark.kind] : null

  return (
    <div className="flex flex-col gap-5">
      {pendingMark && pendingCopy && (
        <Dialog
          isOpen
          handleClose={clearPendingMark}
          closeOnClickOutside
          width={420}
          classes={{
            container:
              "relative rounded-2xl border border-white/10 bg-system-blue-900 p-5 text-left",
          }}
        >
          <button
            type="button"
            aria-label="Close"
            className="absolute right-3 top-3 text-system-gray-400 hover:text-white"
            onClick={clearPendingMark}
          >
            ✕
          </button>
          <h3 className="pr-6 text-base font-semibold text-white">
            {pendingMark.alreadyMarked
              ? pendingCopy.alreadyTitle
              : pendingCopy.title}
          </h3>
          <p className="mt-2 text-sm text-system-gray-300">
            {pendingMark.adName ?? "Ad"}{" "}
            <span className="font-mono text-[11px] text-system-gray-500">
              {pendingMark.adId}
            </span>
          </p>
          <p className="mt-2 text-xs text-system-gray-400">
            {pendingMark.alreadyMarked
              ? pendingCopy.alreadyBody
              : pendingCopy.body}
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={clearPendingMark}
            >
              {pendingMark.alreadyMarked ? "Close" : "Cancel"}
            </button>
            {!pendingMark.alreadyMarked && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={pendingMark.kind === "done" ? markingDone : marking}
                onClick={async () => {
                  await (pendingMark.kind === "done" ? markDone : mark)(
                    pendingMark.adId,
                    pendingMark.adName,
                  )
                  clearPendingMark()
                }}
              >
                {pendingCopy.action}
              </button>
            )}
          </div>
        </Dialog>
      )}
      {result.coverage?.utms && <UtmCoverageLine utms={result.coverage.utms} />}
      {utmSuggestions.size > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-white/10 px-3 py-2">
          <p className="text-[11px] text-system-gray-300">
            {utmSuggestions.size} ad{utmSuggestions.size === 1 ? "" : "s"} with
            URL parameters off the company pattern — the expected url_tags are
            on each finding below.
          </p>
          <button
            type="button"
            className="btn btn-primary btn-xs whitespace-nowrap"
            disabled={applyingUtms}
            onClick={() => applyUtms([...utmSuggestions.keys()])}
          >
            {applyingUtms
              ? "Applying…"
              : `Apply suggested UTMs to ${utmSuggestions.size} ad${utmSuggestions.size === 1 ? "" : "s"}`}
          </button>
        </div>
      )}
      {byAd.size === 0 ? (
        <p className="text-sm text-system-gray-400">
          No findings on this run
          {result.ignored?.findingCount
            ? ` (${result.ignored.findingCount} hidden on ignored ads)`
            : ""}
          {result.done?.skippedCount
            ? ` · ${result.done.skippedCount} ad${result.done.skippedCount === 1 ? "" : "s"} marked done skipped`
            : ""}
          .
        </p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-[11px] uppercase tracking-wide text-system-gray-500">
              <th className="py-2 pr-3 font-medium">Ad</th>
              <th className="py-2 pr-3 font-medium">Findings</th>
              <th className="py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {[...byAd.entries()].map(([adId, { adName, findings }]) => {
              const ignored = ignoredIds.has(adId)
              const done = doneIds.has(adId)
              return (
                <tr
                  key={adId}
                  className={`border-b border-white/5 last:border-0 ${done ? "opacity-50" : ""}`}
                >
                  <td className="py-2 pr-3 align-top">
                    <div className="text-xs text-system-gray-200">{adName}</div>
                    <div className="font-mono text-[11px] text-system-gray-500">
                      {adId}
                      {result.statusByAdId?.[adId]
                        ? ` · ${result.statusByAdId[adId]}`
                        : ""}
                    </div>
                  </td>
                  <td className="py-2 pr-3 align-top">
                    <div className="space-y-1">
                      {findings.map((f, i) => (
                        <div
                          key={`${f.code}-${i}`}
                          className={`text-[11px] text-system-gray-300 ${done ? "line-through" : ""}`}
                        >
                          <span
                            className={`mr-1.5 rounded-full px-1.5 py-0.5 ${SEVERITY_META[f.severity].className}`}
                          >
                            {SEVERITY_META[f.severity].label}
                          </span>
                          {f.postLocked && (
                            <span
                              className="mr-1.5 rounded-full bg-white/10 px-1.5 py-0.5 text-system-gray-400"
                              title="Comes from the ad's existing post — not editable on the ad; edit or replace the post."
                            >
                              🔒 Post-locked
                            </span>
                          )}
                          <span className="font-mono">{f.code}</span> —{" "}
                          {f.message}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 align-top text-right">
                    {done ? (
                      <div className="flex flex-col items-end gap-1">
                        <span className="rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[11px] text-emerald-300">
                          ✓ Done
                        </span>
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs whitespace-nowrap"
                          disabled={reopening}
                          onClick={() => reopen(adId)}
                        >
                          Re-open
                        </button>
                      </div>
                    ) : ignored ? (
                      <span className="text-[11px] text-system-gray-500">
                        Ignored
                      </span>
                    ) : (
                      <div className="flex flex-col items-end gap-1">
                        {utmSuggestions.has(adId) && (
                          <button
                            type="button"
                            className="btn btn-primary btn-xs whitespace-nowrap"
                            disabled={applyingUtms}
                            onClick={() => applyUtms([adId])}
                            title={`Set url_tags to: ${utmSuggestions.get(adId)}`}
                          >
                            Apply suggested UTMs
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-success btn-xs whitespace-nowrap"
                          disabled={markingDone}
                          onClick={() => markDone(adId, adName)}
                          title="Close this ad out — future QA runs skip it entirely"
                        >
                          Mark done
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline btn-xs whitespace-nowrap"
                          disabled={marking}
                          onClick={() => mark(adId, adName)}
                        >
                          Mark irrelevant
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {doneList.length > 0 && (
        <div>
          <div className="mb-1.5 text-[11px] uppercase tracking-wide text-system-gray-500">
            Done ads — skipped by QA runs
          </div>
          <div className="space-y-1">
            {doneList.map(ad => (
              <div
                key={ad.adId}
                className="flex items-center justify-between gap-3 text-[11px] text-system-gray-300"
              >
                <div className="min-w-0 truncate">
                  {ad.adName ?? ad.adId}{" "}
                  <span className="font-mono text-system-gray-500">
                    {ad.adId}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn-outline btn-xs whitespace-nowrap"
                  disabled={reopening}
                  onClick={() => reopen(ad.adId)}
                >
                  Re-open
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {ignoredList.length > 0 && (
        <div>
          <div className="mb-1.5 text-[11px] uppercase tracking-wide text-system-gray-500">
            Ignored ads — still QA-checked, findings hidden from reports
          </div>
          <div className="space-y-1">
            {ignoredList.map(ad => (
              <div
                key={ad.adId}
                className="flex items-center justify-between gap-3 text-[11px] text-system-gray-300"
              >
                <div className="min-w-0 truncate">
                  {ad.adName ?? ad.adId}{" "}
                  <span className="font-mono text-system-gray-500">
                    {ad.adId}
                  </span>
                  {hiddenByAdId.get(ad.adId) ? (
                    <span className="text-system-gray-500">
                      {" "}
                      · {hiddenByAdId.get(ad.adId)} hidden this run
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="btn btn-outline btn-xs whitespace-nowrap"
                  disabled={unmarking}
                  onClick={() => unmark(ad.adId)}
                >
                  Re-include
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// -------------------------------------------------------- analyze-creative ---

/** "Oct 1, 9:30 AM" for a reused report's analyzedAt; empty when unparsable. */
function formatAnalyzedAt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

const MEDIA_TYPE_LABEL: Record<string, string> = {
  video: "Video",
  image: "Image",
  text: "Metadata only",
}

/** Human label for a suggestion's `source` (which signal decided the pick). */
const TAG_SOURCE_LABEL: Record<string, string> = {
  creative: "from the creative",
  ad_name: "from the ad name",
  primary_text: "from the primary text",
  headline: "from the headline",
  description: "from the description",
  link: "from the link",
  mixed: "creative + metadata",
}

type AnalyzeFilter = "all" | "failed" | "untagged" | "text" | "reused" | "kept"

const ANALYZE_FILTER_LABEL: Record<AnalyzeFilter, string> = {
  all: "All",
  failed: "Failed",
  untagged: "No tags written",
  text: "Metadata only",
  reused: "Reused",
  kept: "Kept human tags",
}

/** The tag step ran for this row (autoApplyTags was on and the analysis succeeded). */
const hasTagStep = (row: AnalyzeCreativeResultRow) =>
  row.tagsApplied != null || !!row.tagApplyError || !!row.tagApplySkippedReason

const matchesAnalyzeFilter = (
  row: AnalyzeCreativeResultRow,
  filter: AnalyzeFilter,
): boolean => {
  switch (filter) {
    case "failed":
      return !!row.error
    case "untagged":
      return !row.error && hasTagStep(row) && !(row.tagsApplied ?? 0)
    case "text":
      return row.mediaType === "text"
    case "reused":
      return !!row.reusedSavedAnalysis
    case "kept":
      return !!row.tagsKept?.length
    default:
      return true
  }
}

type PillTone = "green" | "gray" | "red" | "amber" | "sky"

const PILL_TONE_CLS: Record<PillTone, string> = {
  green: "bg-emerald-500/20 text-emerald-300",
  gray: "bg-white/10 text-system-gray-300",
  red: "bg-rose-500/20 text-rose-300",
  amber: "bg-amber-500/20 text-amber-300",
  sky: "bg-sky-500/20 text-sky-300",
}

function Pill({
  tone,
  title,
  children,
}: {
  tone: PillTone
  title?: string
  children: React.ReactNode
}) {
  return (
    <span
      title={title}
      className={`inline-block rounded-full px-2 py-0.5 text-[11px] ${PILL_TONE_CLS[tone]}`}
    >
      {children}
    </span>
  )
}

function StatTile({
  label,
  value,
  tone = "gray",
}: {
  label: string
  value: number
  tone?: PillTone
}) {
  const valueCls =
    tone === "gray" ? "text-white" : PILL_TONE_CLS[tone].split(" ")[1]
  return (
    <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2">
      <div className={`text-lg font-semibold leading-tight ${valueCls}`}>
        {formatNumber(value)}
      </div>
      <div className="text-[11px] text-system-gray-500">{label}</div>
    </div>
  )
}

function Section({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="mb-1 text-[11px] uppercase tracking-wide text-system-gray-500">
        {label}
      </div>
      {children}
    </div>
  )
}

/**
 * One ad of a bulk creative analysis, in full: how it was analyzed (fresh,
 * reused from a saved report, or failed), where the report went, every tag
 * the auto-tagger wrote, every category it left to a person, every pick it
 * could not write — and the Gemini report itself on demand. The point
 * (MD-5613) is that "this ad got no tags" is never a mystery.
 */
function AnalyzeCreativeCard({ row }: { row: AnalyzeCreativeResultRow }) {
  const [showReport, setShowReport] = useState(false)
  const status = row.error
    ? "failed"
    : row.reusedSavedAnalysis
      ? "reused"
      : "analyzed"
  const tagStep = hasTagStep(row)
  const suggestions = row.suggestedTags ?? []
  const writtenCount = row.tagsApplied ?? 0

  return (
    <div className="rounded-md border border-white/10 bg-black/20 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm text-system-gray-100">
            {row.adName || "(unnamed ad)"}
          </div>
          <div className="font-mono text-[11px] text-system-gray-500">
            {row.adId}
            {row.assetId ? ` · asset ${row.assetId}` : ""}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {status === "failed" && <Pill tone="red">Failed</Pill>}
          {status === "analyzed" && <Pill tone="green">Analyzed</Pill>}
          {status === "reused" && (
            <Pill
              tone="sky"
              title={
                row.analyzedAt
                  ? `Saved report from ${formatAnalyzedAt(row.analyzedAt)}`
                  : "Saved report reused"
              }
            >
              Reused
              {row.analyzedAt ? ` · ${formatAnalyzedAt(row.analyzedAt)}` : ""}
            </Pill>
          )}
          {row.mediaType && (
            <Pill
              tone={row.mediaType === "text" ? "amber" : "gray"}
              title={
                row.mediaType === "text"
                  ? "The creative could not be read; the report rests on the ad name and copy."
                  : undefined
              }
            >
              {MEDIA_TYPE_LABEL[row.mediaType] ?? row.mediaType}
            </Pill>
          )}
          {row.suggestedMediaCategory && (
            <Pill tone="gray" title="Media category the model suggested">
              {row.suggestedMediaCategory}
            </Pill>
          )}
          {row.saved && <Pill tone="gray">Saved</Pill>}
          {row.saveError && (
            <Pill tone="red" title={row.saveError}>
              Not saved
            </Pill>
          )}
        </div>
      </div>

      {row.error ? (
        <div className="mt-2 text-xs text-rose-300">{row.error}</div>
      ) : (
        <div className="mt-3 flex flex-col gap-3">
          {row.saveError && (
            <div className="text-[11px] text-rose-300">
              Not saved: {row.saveError}
            </div>
          )}

          {tagStep ? (
            <>
              <Section label={`Tags written (${writtenCount})`}>
                {row.tagsWritten?.length ? (
                  <div className="flex flex-wrap gap-1">
                    {row.tagsWritten.map(tag => (
                      <Pill key={tag} tone="green">
                        {tag}
                      </Pill>
                    ))}
                  </div>
                ) : writtenCount > 0 ? (
                  <div className="text-xs text-emerald-300">
                    {writtenCount} tag{writtenCount === 1 ? "" : "s"} written
                  </div>
                ) : (
                  <div className="text-xs text-amber-300">None</div>
                )}
              </Section>
              {!!row.tagsKept?.length && (
                <Section label="Left as a person tagged them">
                  <div className="flex flex-wrap gap-1">
                    {row.tagsKept.map(category => (
                      <Pill
                        key={category}
                        tone="gray"
                        title="This category already had a tag from a person; the AI pick was not written over it."
                      >
                        {category}
                      </Pill>
                    ))}
                  </div>
                </Section>
              )}
              {row.tagApplySkippedReason && (
                <div className="text-xs text-amber-300">
                  {row.tagApplySkippedReason}
                </div>
              )}
              {row.tagApplyError && (
                <Section label="Could not write">
                  <div className="text-xs text-rose-300">
                    {row.tagApplyError}
                  </div>
                </Section>
              )}
            </>
          ) : null}

          {suggestions.length > 0 && (
            <Section
              label={
                tagStep
                  ? `Model suggestions (${suggestions.length})`
                  : `Suggested tags — not applied (${suggestions.length})`
              }
            >
              <ul className="flex flex-col gap-1 text-xs text-system-gray-200">
                {suggestions.map(suggestion => (
                  <li
                    key={suggestion.category}
                    className="flex flex-wrap items-baseline gap-x-2"
                  >
                    <span className="text-system-gray-400">
                      {suggestion.category}:
                    </span>
                    <span>{suggestion.options.join(", ")}</span>
                    {suggestion.source && (
                      <span className="text-[11px] text-system-gray-500">
                        {TAG_SOURCE_LABEL[suggestion.source] ??
                          suggestion.source}
                      </span>
                    )}
                    {suggestion.reason && (
                      <span className="text-[11px] italic text-system-gray-500">
                        — {suggestion.reason}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {suggestions.length === 0 && !tagStep && (
            <div className="text-xs text-system-gray-500">
              No tag suggestions matched the company taxonomy.
            </div>
          )}

          {row.analysis && (
            <div>
              <button
                type="button"
                className="text-xs text-sky-300 underline"
                onClick={() => setShowReport(open => !open)}
              >
                {showReport ? "Hide full report" : "Show full report"}
              </button>
              {showReport && (
                <div className="mt-2 max-h-96 overflow-auto rounded-md border border-white/10 bg-black/30 p-3 text-sm text-system-gray-200">
                  <MarkdownContent>{row.analysis}</MarkdownContent>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function AnalyzeCreativeRows({ job }: { job: JobRunView }) {
  const [filter, setFilter] = useState<AnalyzeFilter>("all")
  const result = job.result as AnalyzeCreativeJobResult | null
  const rows = result?.results ?? []
  if (rows.length === 0) return <EmptyRows />
  const s = result?.summary
  const withTagStep = s?.tagsApplied != null
  const counts: Record<AnalyzeFilter, number> = {
    all: rows.length,
    failed: rows.filter(r => matchesAnalyzeFilter(r, "failed")).length,
    untagged: rows.filter(r => matchesAnalyzeFilter(r, "untagged")).length,
    text: rows.filter(r => matchesAnalyzeFilter(r, "text")).length,
    reused: rows.filter(r => matchesAnalyzeFilter(r, "reused")).length,
    kept: rows.filter(r => matchesAnalyzeFilter(r, "kept")).length,
  }
  const visible = rows.filter(r => matchesAnalyzeFilter(r, filter))
  const filters = (Object.keys(ANALYZE_FILTER_LABEL) as AnalyzeFilter[]).filter(
    key => key === "all" || counts[key] > 0,
  )

  return (
    <div className="flex flex-col gap-3">
      {s && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
          <StatTile label="Ads" value={s.total} />
          <StatTile label="Analyzed" value={s.analyzed} tone="green" />
          <StatTile label="Reused saved report" value={s.reused} tone="sky" />
          <StatTile
            label="Failed"
            value={s.failed}
            tone={s.failed ? "red" : "gray"}
          />
          <StatTile label="Reports saved" value={s.saved} />
          <StatTile
            label="Metadata only"
            value={counts.text}
            tone={counts.text ? "amber" : "gray"}
          />
          {withTagStep && (
            <>
              <StatTile
                label="Tags written"
                value={s.tagsApplied ?? 0}
                tone="green"
              />
              <StatTile label="Ads tagged" value={s.tagged ?? 0} tone="green" />
              <StatTile
                label="Ads with no tags written"
                value={s.untagged ?? 0}
                tone={s.untagged ? "amber" : "gray"}
              />
              <StatTile label="Ads with human tags kept" value={counts.kept} />
            </>
          )}
        </div>
      )}
      <div className="text-[11px] text-system-gray-500">
        Platform {result?.platform ?? "meta"}
        {withTagStep
          ? " · suggested tags were written as tag assignments (source ai_suggested); categories a person already tagged were left alone"
          : " · suggested tags were not applied — apply them per ad from the drawer"}
        .
      </div>
      <div className="flex flex-wrap gap-1">
        {filters.map(key => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`rounded-full border px-2 py-0.5 text-[11px] ${
              filter === key
                ? "border-white/40 bg-white/10 text-white"
                : "border-white/10 text-system-gray-400 hover:text-white"
            }`}
          >
            {ANALYZE_FILTER_LABEL[key]} · {counts[key]}
          </button>
        ))}
      </div>
      {visible.length === 0 ? (
        <p className="text-sm text-system-gray-400">
          No ads match this filter.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {visible.map((row, i) => (
            <AnalyzeCreativeCard key={`${row.adId}-${i}`} row={row} />
          ))}
        </div>
      )}
    </div>
  )
}

// ----------------------------------------------------------------- generic ---

function GenericRows({ job }: { job: JobRunView }) {
  const rows = job.result?.results
  const count = Array.isArray(rows) ? rows.length : 0
  if (count === 0) return <EmptyRows />
  return (
    <p className="text-sm text-system-gray-400">
      {count} item{count === 1 ? "" : "s"} processed.
    </p>
  )
}

const COMMENT_CATEGORY_LABEL: Record<string, string> = {
  COMPETITOR: "competitor",
  SPAM: "spam",
  NEGATIVE: "negative",
  QUESTION: "question",
  PRAISE: "praise",
  NEUTRAL: "neutral",
  OTHER: "other",
}

/**
 * ad-comment-moderation result: coverage first (posts reached, comments
 * read, posts the credential cannot act for), then what was hidden by
 * category with a sample, and the link to the report where unhide lives.
 */
function CommentModerationRows({ job }: { job: JobRunView }) {
  const r = job.result as unknown as AdCommentModerationResult | null
  if (!r) return <GenericRows job={job} />
  if (r.skipped) {
    return (
      <p className="text-sm text-system-gray-400">
        Skipped — {r.reason ?? r.skipped}.
      </p>
    )
  }
  // Optional on results from before the summary phase shipped.
  const summaries = r.summaries ?? []
  const categories = Object.entries(r.byCategory ?? {})
    .filter(([, n]) => (n ?? 0) > 0)
    .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))
  return (
    <div className="flex flex-col gap-3 text-sm">
      {r.dryRun && (
        <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
          Hiding was off for this run — the comments were judged and recorded
          but nothing was changed on Meta.
        </p>
      )}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Field label="Posts" value={`${r.postsScanned}/${r.postsFound}`} />
        <Field label="Comments read" value={String(r.commentsSeen)} />
        <Field label="New" value={String(r.commentsNew)} />
        <Field
          label={r.dryRun ? "Flagged" : "Hidden"}
          value={String(r.hidden)}
        />
        <Field label="Kept" value={String(r.kept)} />
        <Field label="Hide refused" value={String(r.hideFailed)} />
        <Field label="Unreachable posts" value={String(r.postsUnreachable)} />
        <Field label="AI errors" value={String(r.aiErrors)} />
      </div>
      {categories.length > 0 && (
        <p className="text-xs text-system-gray-300">
          {categories
            .map(
              ([c, n]) =>
                `${n} ${COMMENT_CATEGORY_LABEL[c] ?? c.toLowerCase()}`,
            )
            .join(" · ")}
        </p>
      )}
      {r.unreachable.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-system-gray-500">
            Not reachable — comments there cannot be hidden by Marathon
          </div>
          <ul className="mt-1 flex flex-col gap-1 text-xs text-system-gray-300">
            {r.unreachable.slice(0, 10).map(u => (
              <li key={`${u.platform}:${u.postId}`}>
                {u.platform === "FACEBOOK" ? "FB" : "IG"}{" "}
                <span className="text-system-gray-100">
                  {u.pageName || u.pageId || u.postId}
                </span>
                {u.adNames[0] ? ` · ${u.adNames[0]}` : ""} · ${u.spend} —{" "}
                {u.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {r.samples.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-system-gray-500">
            {r.dryRun ? "Flagged" : "Hidden"} (first{" "}
            {Math.min(r.samples.length, 20)})
          </div>
          <ul className="mt-1 flex flex-col gap-1 text-xs">
            {r.samples.slice(0, 20).map(s => (
              <li
                key={`${s.platform}:${s.commentId}`}
                className="text-system-gray-300"
              >
                <span className="rounded bg-white/10 px-1 text-[10px] uppercase text-system-gray-200">
                  {COMMENT_CATEGORY_LABEL[s.category] ?? s.category}
                </span>{" "}
                {s.author ? (
                  <span className="text-system-gray-400">{s.author}: </span>
                ) : null}
                <span className="text-system-gray-100">“{s.snippet}”</span>{" "}
                <span className="text-system-gray-500">— {s.reason}</span>
                {s.hideError ? (
                  <span className="text-amber-300">
                    {" "}
                    · not hidden: {s.hideError}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
      {summaries.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-system-gray-500">
            What the audience says (AI)
          </div>
          <ul className="mt-1 flex flex-col gap-1 text-xs text-system-gray-300">
            {summaries.map(s => (
              <li key={s.adId}>
                <span className="text-system-gray-100">
                  {s.adName ?? s.adId}
                </span>{" "}
                <span className="text-system-gray-500">({s.commentCount})</span>
                : {s.headline}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Link
        to={`/comment-moderation?from=${encodeURIComponent(r.commentSince)}`}
        className="btn btn-primary btn-sm self-start"
      >
        Open report · unhide
      </Link>
    </div>
  )
}

/** The per-type detailed rows for the job-detail modal body. */
export function JobResultRows({ job }: { job: JobRunView }) {
  if (job.type === "ad-comment-moderation") {
    return <CommentModerationRows job={job} />
  }
  if (job.type === "ad-blast") return <AdBlastRows job={job} />
  if (job.type === "ad-forge-image-gen") return <ImageGenRows job={job} />
  if (job.type === "ad-creative") return <AdCreativeRows job={job} />
  if (job.type === "analyze-creative") return <AnalyzeCreativeRows job={job} />
  if (job.type === "meta-ads-qa") return <MetaAdsQaRows job={job} />
  return <GenericRows job={job} />
}
